import React, { useState } from "react";
import { format } from "date-fns";
import { FiChevronDown } from "react-icons/fi";
import { FaExternalLinkAlt } from "react-icons/fa";

const formatWhen = (value) => {
  if (!value) return "";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch {
    return String(value);
  }
};

const toTrackingHref = (link) => {
  const raw = String(link || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

/**
 * Admin-only carrier partner rows for a parcel (hidden on customer dashboard).
 */
const CarrierPartnerPanel = ({ partners = [], defaultOpen = false }) => {
  const rows = Array.isArray(partners) ? partners : [];
  const [open, setOpen] = useState(defaultOpen);
  if (!rows.length) return null;

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/50 min-w-0 overflow-hidden">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-sky-50/80"
      >
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-sky-800">
            Carrier partner
          </p>
          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border border-sky-200 bg-white text-sky-900">
            {rows.length} record{rows.length === 1 ? "" : "s"}
          </span>
          {rows[0]?.carrierPartnerName ? (
            <span className="text-xs font-semibold text-stone-700 truncate">
              {rows[0].carrierPartnerName}
            </span>
          ) : null}
        </div>
        <FiChevronDown
          className={`shrink-0 text-sky-800 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {open ? (
        <ul className="px-3 pb-3 space-y-2">
          {rows.map((row, idx) => {
            const href = toTrackingHref(row.trackingLink);
            return (
              <li
                key={row.id || `${row.parcelId}-${idx}`}
                className="rounded-lg border border-sky-100 bg-white px-3 py-2.5"
              >
                <p className="font-semibold text-stone-900">
                  {row.carrierPartnerName || "—"}
                </p>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-[6.5rem_minmax(0,1fr)] gap-0.5 sm:gap-2 text-sm">
                  {row.pointName || row.detailsName ? (
                    <>
                      <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                        Point
                      </span>
                      <span className="text-stone-800 break-words">
                        {row.pointName || row.detailsName}
                      </span>
                    </>
                  ) : null}
                  <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Charge
                  </span>
                  <span className="text-stone-900 font-semibold tabular-nums">
                    {Number(row.charge || 0).toFixed(2)}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                    Tracking
                  </span>
                  <span className="min-w-0">
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-blue-700 font-medium hover:underline break-all"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Open link
                        <FaExternalLinkAlt className="text-[10px] shrink-0" />
                      </a>
                    ) : (
                      "—"
                    )}
                  </span>
                  {row.creationDate ? (
                    <>
                      <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                        Date
                      </span>
                      <span className="text-stone-700">
                        {formatWhen(row.creationDate)}
                      </span>
                    </>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default CarrierPartnerPanel;
